import React, { useRef, useState, useEffect } from "react";
import { UploadIcon, FileIcon, TrashIcon } from "../icons/Icons";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

export default function FileUpload({
  label,
  accept = ".png, .jpg, .jpeg",
  maxSize = 5,
  onFileSelect,
  initialFile,
}) {
  const { t } = useTranslation('signup');
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const inputRef = useRef(null);

  useEffect(() => {
    if (initialFile) {
      handleFile(initialFile, true);
    }
  }, [initialFile]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file, skipCallback = false) => {
    if (maxSize && maxSize < file.size / 1024 / 1024) {
      if (!skipCallback) toast.error(t('file_size_exceeds_limit', { maxSize }));
      return;
    }
    if (accept && accept.length > 0) {
      const isAccepted = accept.split(",").some((type) => {
        const trimmedType = type.trim();
        if (trimmedType.startsWith(".")) {
          return file.name.toLowerCase().endsWith(trimmedType.toLowerCase());
        }
        if (trimmedType.endsWith("/*")) {
          const prefix = trimmedType.slice(0, -2);
          return file.type.startsWith(prefix);
        }
        return file.type === trimmedType;
      });

      if (!isAccepted) {
        toast.error(t('file_type_not_supported'));
        return;
      }
    }

    if (onFileSelect) {
      onFileSelect(file);
    }
    setSelectedFile(file);

    // Create preview
    if (file.type.startsWith("image/")) {
      const objectUrl = URL.createObjectURL(file);
      setPreview(objectUrl);
    } else {
      setPreview(null);
    }
  };

  const removeFile = (e) => {
    e.stopPropagation();
    setSelectedFile(null);
    setPreview(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    if (onFileSelect) {
      onFileSelect(null);
    }
  };

  const onButtonClick = () => {
    inputRef.current.click();
  };

  return (
    <div className="field-block grid-full">
      {label && <label className="step-label">{label}</label>}
      {!selectedFile ? (
        <div
          className={`file-upload-area ${dragActive ? "drag-active" : ""}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={onButtonClick}
        >
          <input
            ref={inputRef}
            type="file"
            className="input-file-element"
            accept={accept}
            onChange={handleChange}
            style={{ display: "none" }}
          />
          <div className="upload-icon">
            <UploadIcon />
          </div>
          <p className="upload-text">{t('drag_file_here')}</p>
          <p className="upload-hint">{t('file_size_limit', { maxSize })}</p>
        </div>
      ) : (
        <div className="uploaded-files">
          <div className="uploaded-file-item">
            <div className="file-icon-wrapper">
              {preview ? (
                <img src={preview} alt="Preview" className="file-preview-img" />
              ) : (
                <FileIcon width="24" height="24" />
              )}
            </div>
            <div className="file-info">
              <p className="file-name">{selectedFile.name}</p>
              <p className="file-size">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
            <button type="button" className="remove-btn" onClick={removeFile}>
              <TrashIcon width="18" height="18" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
